import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Phone,
  MessageCircle,
  ChevronLeft,
  Play,
  CheckCircle,
  Navigation,
  Map as MapIcon,
  Shield,
  Star,
  AlertCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { driverApi, passengerApi } from "@/lib/api";
import { GoogleMapView } from "@/components/GoogleMapView";
import { useSocket } from "@/hooks/useSocket";
import { cn } from "@/lib/utils";

import { AppHeader, BottomNav } from "@/components/app-header";

const formatAddress = (val: any): string => {
  if (!val) return "Unknown location";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val.address) return formatAddress(val.address);
    if (val.name) return val.name;
  }
  return "Unknown location";
};

export default function DriverTripControl() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  const { on, off } = useSocket();

  const [trip, setTrip] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTrip = async () => {
    try {
      if (!tripId) return;
      const response: any = await passengerApi.getRide(tripId);
      if (response.trip) {
        setTrip(response.trip);
      }
    } catch (error) {
      console.error("Failed to fetch trip", error);
      toast.error("Failed to load trip details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrip();

    // Real-time status sync if passenger cancels or other events
    on("TRIP_CANCELLED", (data) => {
      if (data.rideId === tripId) {
        toast.error("Passenger cancelled the trip");
        navigate("/driver/trips");
      }
    });

    return () => {
      off("TRIP_CANCELLED");
    };
  }, [tripId]);

  const handleStartTrip = async () => {
    setActionLoading(true);
    try {
      if (!tripId) return;
      await driverApi.startTrip(tripId);
      toast.success("Trip started! Drive safely.");
      fetchTrip();
    } catch (error: any) {
      toast.error(error.message || "Failed to start trip");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndTrip = async () => {
    setActionLoading(true);
    try {
      if (!tripId) return;
      await driverApi.completeTrip(tripId);
      toast.success("Trip completed! Earnings updated.");
      fetchTrip();
      // Stay on page for 3 seconds then go to history
      setTimeout(() => navigate("/driver/trips"), 3000);
    } catch (error: any) {
      toast.error(error.message || "Failed to end trip");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-3 border-yenko-blue/30 border-t-yenko-blue rounded-full animate-spin" />
      </div>
    );
  }

  if (!trip)
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 font-bold">Trip not found</p>
          <Button variant="link" onClick={() => navigate("/driver/trips")}>
            Return to Trips
          </Button>
        </div>
      </div>
    );

  const isActive = [
    "driver_assigned",
    "en_route",
    "arrived",
    "started",
  ].includes(trip.status);

  return (
    <div className="min-h-screen bg-gray-50 pb-40">
      <AppHeader title="Trip Status" showBack />

      <div className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Live Map View */}
        <div className="h-64 rounded-2xl overflow-hidden border border-gray-100 shadow-sm relative">
          <GoogleMapView
            pickup={
              trip?.pickup?.lat
                ? {
                    lat: trip.pickup.lat,
                    lng: trip.pickup.lng,
                    address: trip.pickup.address,
                  }
                : undefined
            }
            destination={
              trip?.destination?.lat
                ? {
                    lat: trip.destination.lat,
                    lng: trip.destination.lng,
                    address: trip.destination.address,
                  }
                : undefined
            }
          />
        </div>

        {/* Passenger Info Card */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-yenko-blue to-blue-600 rounded-xl flex items-center justify-center text-white text-xl font-bold shadow-md">
                {trip.passenger?.full_name?.charAt(0) || "P"}
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">
                  {trip.passenger?.full_name || "Passenger"}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <div className="flex items-center gap-1 bg-yellow-400/10 px-1.5 py-0.5 rounded-md border border-yellow-200">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span className="text-[10px] font-black text-yellow-700">
                      {(trip.passenger?.rating || 5.0).toFixed(1)}
                    </span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                    Verified
                  </span>
                </div>
              </div>
            </div>
            <div
              className={cn(
                "px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest uppercase",
                trip.status === "completed"
                  ? "bg-green-50 text-green-700"
                  : "bg-blue-50 text-blue-700",
              )}
            >
              {trip.status.replace("_", " ")}
            </div>
          </div>

          <div className="flex gap-2">
            <Button className="flex-1 h-12 rounded-xl bg-gray-900 text-white font-bold text-sm shadow-sm hover:bg-gray-800 transition-all">
              <Phone className="w-4 h-4 mr-2" /> Call
            </Button>
            <Button
              variant="outline"
              className="flex-1 h-12 rounded-xl border-gray-100 font-bold text-sm hover:bg-gray-50 transition-all text-gray-600"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Message
            </Button>
          </div>
        </div>

        {/* Route Details Card */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-5 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-yenko-blue" />
            Route Statistics
          </h3>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center border border-blue-100">
                <MapPin className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-0.5">
                  Start From
                </p>
                <p className="font-bold text-gray-900">
                  {formatAddress(trip.pickup)}
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-2xl bg-yellow-50 flex items-center justify-center border border-yellow-100">
                <MapPin className="w-5 h-5 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-0.5">
                  Destination
                </p>
                <p className="font-bold text-gray-900">
                  {formatAddress(trip.destination)}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-50 grid grid-cols-2 gap-3">
            <div className="bg-gray-50/50 p-3 rounded-xl border border-gray-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                Distance
              </p>
              <p className="text-lg font-bold text-gray-900">
                {trip.distance_km || "12.4"}{" "}
                <span className="text-xs font-medium text-gray-400">km</span>
              </p>
            </div>
            <div className="bg-gray-50/50 p-3 rounded-xl border border-gray-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">
                Price
              </p>
              <p className="text-lg font-bold text-yenko-blue">
                ₵{(trip.offer_price || trip.estimated_price).toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Safety Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-4 text-white flex items-center gap-4 shadow-sm">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <p className="font-bold text-sm">Safety Protocol Active</p>
            <p className="text-[11px] text-blue-100/80 leading-tight">
              Your location is shared for security monitoring.
            </p>
          </div>
        </div>
      </div>

      {/* Floating Action Menu */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-md border-t border-gray-100 z-50">
        <div className="max-w-2xl mx-auto">
          {trip.status === "completed" ? (
            <div className="bg-green-600 text-white rounded-2xl h-14 flex items-center justify-center gap-2 shadow-sm animate-fade-in-up">
              <CheckCircle className="w-5 h-5" />
              <p className="font-bold">Trip Completed</p>
            </div>
          ) : (
            <div className="flex gap-2">
              {trip.status === "started" ? (
                <Button
                  onClick={handleEndTrip}
                  disabled={actionLoading}
                  className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold text-base shadow-sm active:scale-95 transition-all"
                >
                  {actionLoading ? "Ending..." : "End Trip & Confirm Fare"}
                </Button>
              ) : (
                <Button
                  onClick={handleStartTrip}
                  disabled={actionLoading}
                  className="w-full h-14 bg-yenko-blue hover:bg-blue-600 text-white rounded-xl font-bold text-base shadow-sm active:scale-95 transition-all"
                >
                  <Play className="w-5 h-5 mr-2 fill-current" />
                  {actionLoading ? "Starting..." : "Start Trip"}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .h-18 { height: 4.5rem; }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
      `,
        }}
      />
    </div>
  );
}
